import { Payload } from 'echarts';
/* eslint-disable import/no-cycle */
/* eslint-disable no-param-reassign */
/* eslint-disable sonarjs/cognitive-complexity */
// ECharts 5.4.0
// ECharts默认的散点高亮，是通过mouseover实现的，取消高亮是通过mouseout实现，PC端没有点击高亮
// 移动端没有mousemove事件，所以在点击时触发了上述俩个交互，从而有点击高亮

// 在PC端，需要实现点击高亮，但点击高亮后，移出气泡会触发mouseout，导致取消高亮，所以需要off掉mouseout事件

// 在移动端，需要实现临近搜索，临近搜索的高亮状态与点击高亮的高亮状态是不一致的，从而导致俩种状态的高亮无法共存，比如先临近搜索高亮一个点
// 然后点击高亮一个点，会发现该点竟然进入了blur状态, 其原因是因为ECharts的state切换一定是闭环的，即一定是退出高亮态才能重新进入高亮态
// 所以在移动端，需要在off掉mouseout的基础上，off掉mouseover事件，从而自己实现与控制点击高亮

// 综上，需要屏蔽掉原有的mouseover和mouseout事件的highlight与downpaly操作，然后自己实现相关交互
// 通过设置__highDownDispatcher属性为false屏蔽原生交互，自己代理新的dvHighlight与dvDownplay

/* eslint-disable complexity */
import merge from 'deepmerge';
import { getInstanceById } from 'echarts/core';
import { type EChartsExtensionInstallRegisters } from 'echarts/types/src/extension';
import type GlobalModel from 'echarts/types/src/model/Global';
import { getECData } from 'echarts/lib/util/innerStore';
import ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import { isStateContainEffectStyle, isMobile } from '../../../util/utils';
import { DvScatterOption } from '../../../core/option';
import { findNearestPointIndex } from '../../../helper/dvScatter';
import { type ActionPayloadApplication, stateHandle, getItemsInfo } from '../../../util/action';
import { isNumber } from '../../../util/math';
// 判断全局是否已经注册过事件
const isRegisterAction: Record<string, boolean> = {};
let currentChartId = '';

// 判断select是否已经注册过事件
const isSelectRegisterAction: Record<string, boolean> = {};

// 没有配置【保持高亮】的series
let hasNotKeepHighlightSeriesIndex: any = [];
// 统计临近搜索的触发方式
let moveNearSearchSeriesIndex: any = [];
let clickNearSearchSeriesIndex: any = [];
// 自动取消高亮的timer
let autoHideTimer: any = null;
// 判断自动取消高亮的不交互判定时间
const autoHideDelay = 2000;
// 自动取消高亮的参数集
let autoHidePayload: ActionPayloadApplication | null = null;

/**
 * 预处理图形，添加自定义states
 * @param el 图形元素
 */
function dvScatterStatePreprocess(el: any) {
  // 预处理，生成新的state
  const normalStyle = el._normalState ? el._normalState.style : el.style;
  el.states.dvemphasis = merge({ style: normalStyle }, el.states.emphasis || {});
  el.states.dvblur = merge({ style: normalStyle }, el.states.blur || {});
  el.states.dvselect = merge(
    { style: normalStyle },
    (isStateContainEffectStyle(el.states.select) && el.states.select) || el.states.emphasis || {}
  );
  el.states.dvunselected = merge(
    { style: normalStyle },
    (isStateContainEffectStyle(el.states.unselect) && el.states.unselect) || el.states.blur || {}
  );

  if (el._textContent) {
    const textContentNormalStyle = el._textContent._normalState
      ? el._textContent._normalState.style
      : el._textContent.style;
    el._textContent.states.dvemphasis = merge(
      { style: textContentNormalStyle },
      el._textContent.states.emphasis || {}
    );
    el._textContent.states.dvselect = merge(
      { style: textContentNormalStyle },
      (isStateContainEffectStyle(el._textContent.states.select) && el._textContent.states.select) ||
        el._textContent.states.emphasis ||
        {}
    );
    el._textContent.states.dvblur = merge(
      { style: textContentNormalStyle },
      el._textContent.states.blur || {}
    );
    el._textContent.states.dvunselected = merge(
      { style: textContentNormalStyle },
      (isStateContainEffectStyle(el._textContent.states.unselect) &&
        el._textContent.states.unselect) ||
        el._textContent.states.blur ||
        {}
    );
  }

  // 删除原有状态以屏蔽ECharts的各种默认交互
  // 默认交互将会在actionProxy中通过事件代理转移到新的交互函数中
  delete el.states.emphasis;
  delete el.states.blur;
  delete el.states.select;
  delete el.states.unselected;
  if (el._textContent) {
    delete el._textContent.states.emphasis;
    delete el._textContent.states.blur;
    delete el._textContent.states.select;
    delete el._textContent.states.unselected;
  }
}

function dvHighlight(payload: ActionPayloadApplication, ecModel: GlobalModel, api: ExtensionAPI) {
  api.getZr().wakeUp();
  stateHandle(payload, ecModel, api, 'add', 'dvemphasis');
  stateHandle({ ...payload, reverse: true }, ecModel, api, 'add', 'dvblur', 'dvselect');
}

function dvDownplay(payload: ActionPayloadApplication, ecModel: GlobalModel, api: ExtensionAPI) {
  api.getZr().wakeUp();
  stateHandle(payload, ecModel, api, 'remove', ['dvemphasis', 'dvblur']);
}

function dvUnselect(payload: ActionPayloadApplication, ecModel: GlobalModel, api: ExtensionAPI) {
  api.getZr().wakeUp();
  stateHandle(payload, ecModel, api, 'remove', ['dvselect', 'dvunselected']);
}

// helper of dvSelect
function autoHide(payload: ActionPayloadApplication, ecModel: GlobalModel, api: ExtensionAPI) {
  const ecIns = getInstanceById(api.getId());
  const owner = ecIns.$dvGetOwner();
  autoHidePayload = payload;
  if (!isSelectRegisterAction[api.getId()]) {
    api.on('dvselect', () => {
      clearTimeout(autoHideTimer);
      const ecModelTemp = api.getModel();
      // 获取选中点
      const result = getItemsInfo(autoHidePayload, ecModelTemp);
      // 涉及的series
      const seriesIndexes = Array.from(new Set(result.map(d => d.seriesIndex)));
      autoHideTimer = setTimeout(() => {
        // 筛选出没有配置【保持高亮】且配置了【自动取消高亮】的series
        const targetSeries = seriesIndexes.filter((seriesIndex: number) => {
          const seriesItemOption = ecModelTemp.getOption().series[seriesIndex];
          return (
            seriesItemOption &&
            !seriesItemOption.dvKeepHighlight &&
            seriesItemOption.dvAutoHide &&
            seriesItemOption.dvAutoHide.enable
          );
        });

        if (!targetSeries.length) {
          // 即使没有找到，也要触发事件，用户可能希望对【保持高亮】的series或者其他情况做额外处理
          owner.emit('dv:autoHideScatter', targetSeries);
          return;
        }
        const unselectPayload = { type: 'dvUnselect', seriesIndex: targetSeries };
        dvUnselect(unselectPayload, ecModelTemp, api);
        owner.emit('dv:autoHideScatter', targetSeries);
      }, autoHideDelay);
    });

    isSelectRegisterAction[api.getId()] = true;
  }
}

function dvSelect(payload: ActionPayloadApplication, ecModel: GlobalModel, api: ExtensionAPI) {
  api.getZr().wakeUp();
  // 获取选中点
  const result = getItemsInfo(payload, ecModel);

  //清空选中点的unselect状态，避免新选中的点同时含有select与unselect
  stateHandle(payload, ecModel, api, 'remove', 'dvunselected');

  // 非多选且有选中时，清空其他点的select状态
  if (result.length && !payload.isMultiple) {
    stateHandle({ ...payload, reverse: true }, ecModel, api, 'remove', 'dvselect');
  }

  stateHandle(payload, ecModel, api, 'add', 'dvselect');
  stateHandle(
    { ...payload, reverse: true },
    ecModel,
    api,
    'add',
    'dvunselected',
    payload.isMultiple ? 'dvselect' : null
  );

  // 自动取消高亮
  autoHide(payload, ecModel, api);
}

function dvEmphasis(payload: ActionPayloadApplication, ecModel: GlobalModel, api: ExtensionAPI) {
  api.getZr().wakeUp();
  stateHandle(payload, ecModel, api, 'add', 'dvemphasis');
}

function dvBlur(payload: ActionPayloadApplication, ecModel: GlobalModel, api: ExtensionAPI) {
  api.getZr().wakeUp();
  stateHandle(payload, ecModel, api, 'add', 'dvblur');
}

function selectHandle(
  series: DvScatterOption[],
  seriesIndex: number,
  dataIndex: number,
  chart: ExtensionAPI
) {
  hasNotKeepHighlightSeriesIndex = [];
  series.forEach((seriesItem, seriesIdx) => {
    if (!seriesItem.dvKeepHighlight) {
      hasNotKeepHighlightSeriesIndex.push(seriesIdx);
    }
  });

  if (isNumber(seriesIndex) && isNumber(dataIndex) && seriesIndex > -1 && dataIndex > -1) {
    // 有目标时触发高亮
    chart.dispatchAction({
      type: 'dvSelect',
      dataIndex,
      seriesIndex,
      isMultiple: series[seriesIndex].dvMultipleSelect
    });
  } else if (hasNotKeepHighlightSeriesIndex.length) {
    // 没有目标时，取消高亮所有没有配置【保持高亮】的series
    chart.dispatchAction({
      type: 'dvUnselect',
      seriesIndex: hasNotKeepHighlightSeriesIndex
    });
  }
}

function nearSearchHandle(
  seriesIndexs: number[],
  point: [number, number],
  series: DvScatterOption[],
  owner: any,
  chart: ExtensionAPI,
  actionType: string
) {
  const result = [];
  const [offsetX, offsetY] = point;
  const ecIns = getInstanceById(chart.getId());
  seriesIndexs.forEach(seriesIndex => {
    const itemResult = findNearestPointIndex({ x: offsetX, y: offsetY }, ecIns, seriesIndex);
    if (itemResult.index >= 0) {
      result.push({
        seriesIndex,
        dataIndex: itemResult.index,
        distance: itemResult.distance
      });
    }
  });

  result.sort((a, b) => a.distance - b.distance);
  const { dataIndex, seriesIndex } = result[0] || {};
  owner.emit('dv:nearSearch', { dataIndex, seriesIndex, trigger: actionType, point });
  if (isNumber(dataIndex) && isNumber(seriesIndex) && dataIndex >= 0 && seriesIndex >= 0) {
    // 当选中气泡，且鼠标不在气泡上时，手动触发tooltip
    const seriesModel = chart.getModel().getSeriesByIndex(seriesIndex);
    const itemGraph = seriesModel.getData().getItemGraphicEl(dataIndex);
    let graphContainPoint = false;
    if (itemGraph.isGroup) {
      itemGraph.eachChild(graph => {
        // 只对主图形进行判断
        if (graph.extra?.isMain && graph.contain && graph.contain(offsetX, offsetY)) {
          graphContainPoint = true;
        }
      });
    } else {
      graphContainPoint = itemGraph.contain && itemGraph.contain(offsetX, offsetY);
    }

    if (!graphContainPoint) {
      chart.dispatchAction({
        type: 'showTip',
        seriesIndex,
        dataIndex
      });
    }
  }
  selectHandle(series, seriesIndex, dataIndex, chart);
}

function pcActionHandle(chart: ExtensionAPI) {
  // 移入高亮
  chart.on('mouseover', params => {
    const { seriesIndex, dataIndex } = params;
    if (!isNumber(seriesIndex) || !isNumber(dataIndex)) {
      return;
    }
    chart.dispatchAction({
      type: 'dvHighlight',
      seriesIndex,
      dataIndex
    });
  });

  // 移出取消高亮
  chart.on('mouseout', params => {
    const { seriesIndex, dataIndex } = params;
    if (!isNumber(seriesIndex) || !isNumber(dataIndex)) {
      return;
    }
    chart.dispatchAction({
      type: 'dvDownplay'
    });
  });
}

function mobileActionHandle(chart: ExtensionAPI) {
  const option = chart.getOption();
  const ecIns = getInstanceById(chart.getId());
  const { series } = option;
  const owner = ecIns.$dvGetOwner();
  const zrenderInstance = chart.getZr();
  moveNearSearchSeriesIndex = [];
  clickNearSearchSeriesIndex = [];
  // 统计开启临近搜索的series及其触发方式
  series.forEach((seriesItem, seriesIndex) => {
    const { dvNearSearch } = seriesItem;
    const { enable = false, trigger = 'mousemove' } = dvNearSearch || {};
    if (enable) {
      if (trigger === 'click' || trigger === 'all') {
        clickNearSearchSeriesIndex.push(seriesIndex);
      }
      if (trigger === 'mousemove' || trigger === 'all') {
        moveNearSearchSeriesIndex.push(seriesIndex);
      }
    }
  });

  // 根据上面统计的数组，给对应的交互绑定临近搜索
  owner.on('dv:updateAxisPointer', e => {
    const { axesInfo } = e;
    const [xInfo, yInfo] = axesInfo;
    if (xInfo && yInfo) {
      const { value: xValue } = xInfo;
      const { value: yValue } = yInfo;
      const point = ecIns.convertToPixel('grid', [xValue, yValue]);
      nearSearchHandle(
        moveNearSearchSeriesIndex,
        point as [number, number],
        chart.getOption().series as DvScatterOption[],
        owner,
        chart,
        'mousemove'
      );
    }
  });
  zrenderInstance.on('click', e => {
    const { offsetX, offsetY } = e;
    nearSearchHandle(
      clickNearSearchSeriesIndex,
      [offsetX, offsetY],
      chart.getOption().series as DvScatterOption[],
      owner,
      chart,
      'click'
    );
  });
}

// TODO:移动到util中
// 事件代理，点击子元素时触发group的事件
function findTopElement(target: any, deep: number) {
  if (target.isTop) {
    return target;
  }
  if (!target.parent) {
    return null;
  }
  if (deep === MAX_CHILD_DEEP) {
    return null;
  }
  return findTopElement(target.parent, deep + 1);
}

function actionHandle(chart: ExtensionAPI) {
  const zrenderInstance = chart.getZr();
  zrenderInstance.wakeUp();
  // 通过设置__highDownDispatcher属性为false屏蔽原生,预处理自己的状态
  let seriesCount = 0;
  while (chart.getModel().getSeriesByIndex(seriesCount)) {
    const SeriesModel = chart.getModel().getSeriesByIndex(seriesCount);
    const seriesView = chart.getViewOfSeriesModel(SeriesModel);
    seriesView.group.eachChild(el => {
      el.__highDownDispatcher = false;
      dvScatterStatePreprocess(el);
      if (el.isGroup) {
        el.traverse(childEl => {
          childEl.__highDownDispatcher = false;
          dvScatterStatePreprocess(childEl);
        });
      }
    });
    seriesCount += 1;
  }

  if (isRegisterAction[currentChartId]) {
    return;
  }
  // 点击高亮
  zrenderInstance.on('click', e => {
    const { target, offsetX, offsetY } = e;
    const seriesTemp = chart.getOption().series;

    let seriesIndex;
    let dataIndex;
    if (target) {
      // 跳过label的点击事件
      if (target.parent && target.parent.extra && target.parent.extra.isLabel) {
        return;
      }
      // TODO: 点击子元素也应识别为总体气泡
      // const topElement = findTopElement(target, 0);
      // console.log(getECData(topElement));
      // if (!topElement) {
      //   return;
      // }
      const ecData = getECData(target);
      seriesIndex = ecData.seriesIndex;
      dataIndex = ecData.dataIndex;
    }
    const gridModel = chart.getModel().getComponent('grid');
    const gridRect = gridModel.coordinateSystem.getRect();
    // 非坐标系内，return
    if (!gridRect.contain(offsetX, offsetY)) {
      return;
    }
    selectHandle(seriesTemp as DvScatterOption[], seriesIndex, dataIndex, chart);
    // TODO: 如果已经选中，再次点击取消选中
  });

  if (isMobile()) {
    mobileActionHandle(chart);
  } else {
    pcActionHandle(chart);
  }
}

function isSeriesDvScatter(chart: ExtensionAPI, params: Payload) {
  const { seriesIndex, seriesName } = params;
  if (!seriesIndex && !seriesName) {
    return false;
  }
  const seriesOption = chart.getOption().series || [];
  let targetSeries;
  if (seriesIndex) {
    targetSeries = seriesOption[seriesIndex];
  }
  if (seriesName) {
    targetSeries = seriesOption.find(series => series.name === seriesName);
  }
  if (!targetSeries) {
    return false;
  }
  return targetSeries.type === 'custom' && targetSeries.dvType === 'dvScatter';
}
function actionProxy(chart: ExtensionAPI) {
  currentChartId = chart.getId();
  if (isRegisterAction[currentChartId]) {
    return;
  }
  const stateListNeedProxy = ['highlight', 'downplay', 'select', 'unselect', 'emphasis', 'blur'];
  stateListNeedProxy.forEach(state => {
    const proxyStateName = `dv${state.charAt(0).toUpperCase()}${state.slice(1)}`;
    chart.on(state, (params: Payload) => {
      if (!isSeriesDvScatter(chart, params)) {
        return;
      }
      const paramsTemp = {};
      if (params.seriesIndex) {
        paramsTemp.seriesIndex = params.seriesIndex;
      }
      if (params.seriesName) {
        paramsTemp.seriesName = params.seriesName;
      }
      if (params.dataIndex) {
        paramsTemp.dataIndex = params.dataIndex;
      }
      if (params.name) {
        paramsTemp.dataName = params.name;
      }

      chart.dispatchAction({
        type: proxyStateName,
        ...paramsTemp
      });
    });
  });
}

/**
 *
 * @param registers
 */
export function actionExtensionInstall(registers: EChartsExtensionInstallRegisters) {
  // 注册自定义事件
  registers.registerAction(
    {
      type: 'dvHighlight',
      event: 'dvhighlight',
      update: 'none'
    },
    dvHighlight
  );
  registers.registerAction(
    {
      type: 'dvDownplay',
      event: 'dvdownplay',
      update: 'none'
    },
    dvDownplay
  );
  registers.registerAction(
    {
      type: 'dvSelect',
      event: 'dvselect',
      update: 'none'
    },
    dvSelect
  );
  registers.registerAction(
    {
      type: 'dvUnselect',
      event: 'dvunselect',
      update: 'none'
    },
    dvUnselect
  );
  registers.registerAction(
    {
      type: 'dvEmphasis',
      event: 'dvemphasis',
      update: 'none'
    },
    dvEmphasis
  );
  registers.registerAction(
    {
      type: 'dvBlur',
      event: 'dvblur',
      update: 'none'
    },
    dvBlur
  );

  registers.registerUpdateLifecycle('afterupdate', (_, chart) => {
    currentChartId = chart.getId();
    const option = chart.getOption();
    const { series } = option;
    const notBubble = !series.find(item => item.type === 'custom' && item.dvType === 'dvScatter');
    if (notBubble) {
      // 仅在只包含气泡图时生效
      return;
    }
    clearTimeout(autoHideTimer);
    actionHandle(chart);
    actionProxy(chart);
    isRegisterAction[currentChartId] = true;
  });
}
